"use client";

import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";

import { BRAND_CYAN, Btn } from "./shared";

const EXPECTED_COUNT = 10;
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ChallengeContextValue = {
  open: () => void;
  close: () => void;
};

const ChallengeContext = createContext<ChallengeContextValue | null>(null);

export function useChallengeModal() {
  const ctx = useContext(ChallengeContext);
  if (!ctx) {
    throw new Error("useChallengeModal must be used inside ChallengeProvider");
  }
  return ctx;
}

export function ChallengeProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ChallengeContext.Provider value={value}>
      {children}
      {isOpen ? <ChallengeModal onClose={close} /> : null}
    </ChallengeContext.Provider>
  );
}

type UploadInfo = {
  key: string;
  uploadUrl: string;
  name: string;
  contentType: string;
};

type InitResponse = {
  submissionId: string;
  uploads: UploadInfo[];
};

type Status = "idle" | "submitting" | "uploading" | "success" | "error";

const ChallengeModal = ({ onClose }: { onClose: () => void }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && status !== "uploading" && status !== "submitting") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, status]);

  const onPickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list) return;
    const picked = Array.from(list).slice(0, EXPECTED_COUNT);
    const filtered = picked.filter((f) => f.type.startsWith("image/"));
    if (filtered.length !== picked.length) {
      setError("All files must be images.");
    } else {
      setError(null);
    }
    setFiles(filtered);
  };

  const removeFileAt = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit =
    status === "idle" &&
    files.length === EXPECTED_COUNT &&
    EMAIL_RE.test(email) &&
    description.trim().length >= 10 &&
    files.every((f) => f.size <= MAX_FILE_BYTES);

  const submit = async () => {
    setError(null);
    setStatus("submitting");
    setProgress(0);
    try {
      const initRes = await fetch("/api/lens/challenge/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          description,
          files: files.map((f) => ({
            name: f.name,
            type: f.type,
            size: f.size,
          })),
        }),
      });
      if (!initRes.ok) {
        const data = (await initRes.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? "Could not prepare upload.");
      }
      const init = (await initRes.json()) as InitResponse;

      setStatus("uploading");
      let uploaded = 0;
      await Promise.all(
        init.uploads.map(async (u, i) => {
          const file = files[i];
          const res = await fetch(u.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": u.contentType },
            body: file,
          });
          if (!res.ok) {
            throw new Error(`Failed to upload image ${i + 1}.`);
          }
          uploaded += 1;
          setProgress(Math.round((uploaded / init.uploads.length) * 100));
        })
      );

      const completeRes = await fetch("/api/lens/challenge/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId: init.submissionId,
          email,
          description,
          files: init.uploads.map((u) => ({ key: u.key, name: u.name })),
        }),
      });
      if (!completeRes.ok) {
        const data = (await completeRes.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? "Submission failed.");
      }
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const lockClose = status === "submitting" || status === "uploading";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lens-challenge-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "color-mix(in oklab, #0A0B0D 60%, transparent)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !lockClose) onClose();
      }}
    >
      <div
        ref={dialogRef}
        style={{
          width: "min(620px, 100%)",
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          background: "var(--card)",
          color: "var(--card-foreground)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          boxShadow: "0 24px 60px -20px rgba(0,0,0,0.35)",
          position: "relative",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => {
            if (!lockClose) onClose();
          }}
          disabled={lockClose}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 36,
            height: 36,
            borderRadius: 8,
            background: "transparent",
            border: "1px solid var(--border)",
            color: "var(--muted-foreground)",
            cursor: lockClose ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: lockClose ? 0.5 : 1,
          }}
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        {status === "success" ? (
          <SuccessView email={email} onClose={onClose} />
        ) : (
          <div
            style={{
              padding: "32px 32px 28px 32px",
              display: "flex",
              flexDirection: "column",
              gap: 22,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  font: "500 12px/16px var(--font-mono)",
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.04em",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    background: BRAND_CYAN,
                    display: "inline-block",
                  }}
                />
                The 10-image challenge
              </span>
              <h2
                id="lens-challenge-title"
                style={{
                  font: "500 36px/40px 'Newsreader', Georgia, serif",
                  letterSpacing: "-1px",
                  margin: 0,
                }}
              >
                Send us 10 examples.{" "}
                <span style={{ fontStyle: "italic" }}>We&apos;ll do the rest.</span>
              </h2>
              <p
                style={{
                  font: "400 15px/24px var(--font-sans)",
                  color: "var(--muted-foreground)",
                  margin: 0,
                }}
              >
                Drop in 10 labeled images of your task, tell us what success looks
                like, and we&apos;ll fine-tune Moondream on them.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <FieldLabel
                label="Images"
                hint={`${files.length} / ${EXPECTED_COUNT} selected`}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "18px 16px",
                  border: "1px dashed var(--border)",
                  borderRadius: 10,
                  background: "var(--muted-extra)",
                  color: "var(--foreground)",
                  font: "500 14px/20px var(--font-sans)",
                  cursor: "pointer",
                }}
              >
                <ImagePlus size={18} strokeWidth={1.8} />
                {files.length === 0
                  ? `Choose ${EXPECTED_COUNT} images`
                  : files.length < EXPECTED_COUNT
                    ? `Add ${EXPECTED_COUNT - files.length} more`
                    : "Replace selection"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onPickFiles}
                style={{ display: "none" }}
              />
              {files.length > 0 ? (
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 8,
                  }}
                >
                  {files.map((f, i) => (
                    <FilePreview
                      key={`${f.name}-${i}`}
                      file={f}
                      onRemove={() => removeFileAt(i)}
                    />
                  ))}
                </ul>
              ) : null}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <FieldLabel label="Fine-tuning goal" />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="What should Moondream learn? e.g. 'Detect workers without hard hats on construction sites.'"
                style={{
                  width: "100%",
                  resize: "vertical",
                  padding: "12px 14px",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  background: "var(--background)",
                  color: "var(--foreground)",
                  font: "400 14px/22px var(--font-sans)",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <FieldLabel label="Your email" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                style={{
                  width: "100%",
                  height: 44,
                  padding: "0 14px",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  background: "var(--background)",
                  color: "var(--foreground)",
                  font: "400 14px/22px var(--font-sans)",
                  outline: "none",
                }}
              />
            </div>

            {error ? (
              <div
                role="alert"
                style={{
                  padding: "10px 14px",
                  border: "1px solid color-mix(in oklab, #DC2626 35%, transparent)",
                  background: "color-mix(in oklab, #DC2626 8%, var(--background))",
                  color: "#7f1d1d",
                  borderRadius: 10,
                  font: "400 13px/20px var(--font-sans)",
                }}
              >
                {error}
              </div>
            ) : null}

            {status === "uploading" || status === "submitting" ? (
              <ProgressBar
                label={
                  status === "submitting"
                    ? "Preparing upload…"
                    : `Uploading images (${progress}%)`
                }
                progress={status === "submitting" ? 5 : progress}
              />
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "flex-end",
                marginTop: 4,
              }}
            >
              <Btn
                variant="ghost"
                size="md"
                onClick={() => {
                  if (!lockClose) onClose();
                }}
              >
                Cancel
              </Btn>
              <Btn
                variant="primary"
                size="md"
                onClick={canSubmit ? submit : undefined}
                style={
                  canSubmit
                    ? undefined
                    : {
                        opacity: 0.5,
                        pointerEvents: "none",
                        cursor: "not-allowed",
                      }
                }
              >
                {status === "submitting" || status === "uploading" ? (
                  <>
                    <Loader2
                      size={14}
                      strokeWidth={2}
                      style={{ animation: "lens-spin 0.9s linear infinite" }}
                    />
                    Submitting…
                  </>
                ) : (
                  "Submit challenge"
                )}
              </Btn>
            </div>

            <style>{`@keyframes lens-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
};

const FieldLabel = ({ label, hint }: { label: string; hint?: string }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <span
      style={{
        font: "600 12px/16px var(--font-sans)",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--muted-foreground)",
      }}
    >
      {label}
    </span>
    {hint ? (
      <span
        style={{
          font: "500 12px/16px var(--font-mono)",
          color: "var(--muted-foreground)",
        }}
      >
        {hint}
      </span>
    ) : null}
  </div>
);

const FilePreview = ({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <li
      style={{
        position: "relative",
        aspectRatio: "1 / 1",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--border)",
        background: "var(--muted-extra)",
      }}
    >
      {url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt={file.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 22,
          height: 22,
          borderRadius: 99,
          border: "none",
          background: "rgba(10,11,13,0.75)",
          color: "#fff",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <X size={12} strokeWidth={2.2} />
      </button>
    </li>
  );
};

const ProgressBar = ({ label, progress }: { label: string; progress: number }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span
      style={{
        font: "500 12px/16px var(--font-mono)",
        color: "var(--muted-foreground)",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
    <div
      style={{
        height: 6,
        background: "var(--muted)",
        borderRadius: 99,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${Math.max(5, Math.min(100, progress))}%`,
          height: "100%",
          background: BRAND_CYAN,
          transition: "width 200ms ease",
        }}
      />
    </div>
  </div>
);

const SuccessView = ({
  email,
  onClose,
}: {
  email: string;
  onClose: () => void;
}) => (
  <div
    style={{
      padding: "40px 32px 32px 32px",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 16,
    }}
  >
    <span
      style={{
        width: 44,
        height: 44,
        borderRadius: 99,
        background: "color-mix(in oklab, #16A34A 14%, transparent)",
        color: "#16A34A",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckCircle2 size={22} strokeWidth={1.8} />
    </span>
    <h2
      style={{
        font: "500 32px/38px 'Newsreader', Georgia, serif",
        letterSpacing: "-0.8px",
        margin: 0,
      }}
    >
      Got it. <span style={{ fontStyle: "italic" }}>We&apos;ll be in touch.</span>
    </h2>
    <p
      style={{
        font: "400 15px/24px var(--font-sans)",
        color: "var(--muted-foreground)",
        margin: 0,
      }}
    >
      We sent a confirmation to <strong style={{ color: "var(--foreground)" }}>{email}</strong>.
      Our team will review your task and respond within 2 business days.
    </p>
    <div style={{ marginTop: 8 }}>
      <Btn variant="primary" size="md" onClick={onClose}>
        Done
      </Btn>
    </div>
  </div>
);
